import {
  MaskableFieldHeader,
  useMaskableFieldExpanded,
} from "@/components/form-items/MaskableFieldHeader";
import { memo } from "react";
import { FormControl, FormField, FormItem } from "@/components/ui/form";
import InputSelect from "@/components/ui/input-select";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { FormFieldProps } from "./types";
import { Separator } from "@/components/ui/separator";

export type CurrencyOption = {
  label: string;
  value: string;
  sign?: string;
};

export const currencyOptions: CurrencyOption[] = [
  { label: "EUR", value: "EUR", sign: "€" },
  { label: "USD", value: "USD", sign: "$" },
  { label: "CAD", value: "CAD", sign: "C$" },
  { label: "HKD", value: "HKD", sign: "HK$" },
  { label: "AUD", value: "AUD", sign: "A$" },
  { label: "JPY", value: "JPY", sign: "¥" },
  { label: "CNY", value: "CNY", sign: "C¥" },
  { label: "CHF", value: "CHF", sign: "CHF" },
  { label: "GBP", value: "GBP", sign: "£" },
  { label: "DKK", value: "DKK", sign: "kr" },
  { label: "PLN", value: "PLN", sign: "zł" },
  { label: "CZK", value: "CZK", sign: "Kč" },
  { label: "SEK", value: "SEK", sign: "kr" },
  { label: "FRF", value: "FRF", sign: "₣" },
  { label: "ITL", value: "ITL", sign: "₤" },
  { label: "DEM", value: "DEM", sign: "DM" },
  { label: "BEF", value: "BEF", sign: "BF" },
  { label: "NGL", value: "NGL", sign: "ƒ" },
  { label: "LUF", value: "LUF", sign: "F" },
  { label: "ESP", value: "ESP", sign: "₧" },
  { label: "ATS", value: "ATS", sign: "öS" },
  { label: "GRD", value: "GRD", sign: "₯" },
];

const InputSelectField = memo(({ config, control }: FormFieldProps) => {
  const isCurrency = config.type === "inputSelectCurrency";
  const { enabled: maskEnabled, showControl, minimized } =
    useMaskableFieldExpanded(config.name, {
      nonMaskable: config.nonMaskable,
      maskable: config.maskable,
    });
  const useMask = isCurrency && maskEnabled && !config.nonMaskable;

  // Masked → hidden entirely (re-enable it from the field visibility menu).
  if (useMask && minimized) {
    return null;
  }

  const handleChange = (field: any, value: any) => {
    if (config.type === "inputSelectCurrency") {
      field.onChange(
        JSON.stringify({
          currency: value,
          amount: field.value ? JSON.parse(field.value).amount : undefined,
        }),
      );
    } else {
      field.onChange(value);
    }
  };

  const handleInputChange = (
    field: any,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (config.type === "inputSelectCurrency") {
      field.onChange(
        JSON.stringify({
          currency: field.value ? JSON.parse(field.value).currency : undefined,
          amount: e.target.value,
        }),
      );
    }
  };

  return (
    <div>
      <FormField
        control={control}
        name={config.name}
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <div className="flex w-full flex-col gap-1">
                {useMask ? (
                  <MaskableFieldHeader
                    fieldName={config.name}
                    maskable={config.maskable}
                    className="mb-0 w-full"
                    label={config.label}
                  />
                ) : (
                  <Label
                    htmlFor={field.name}
                    className="text-slate-700 text-left text-sm font-medium font-['Raleway'] leading-normal"
                  >
                    {config.label}
                  </Label>
                )}
                <div className={cn(useMask && !showControl && "hidden")}>
                  <InputSelect
                    options={
                      config.type === "inputSelectCurrency"
                        ? currencyOptions
                        : undefined
                    }
                    defaultSelectValue={
                      config.type === "inputSelectCurrency"
                        ? field.value
                          ? JSON.parse(field.value).currency
                          : config.defaultSelectValue
                        : field.value
                    }
                    value={
                      config.type === "inputSelectCurrency" && field.value
                        ? JSON.parse(field.value).amount
                        : undefined
                    }
                    onSelectChange={(e: any) => handleChange(field, e)}
                    onChange={(e: any) => handleInputChange(field, e)}
                    className="w-full"
                    selectClassName="w-[140px]"
                  />
                </div>
              </div>
            </FormControl>
          </FormItem>
        )}
      />
      {config.separator && <Separator className="my-4" />}
    </div>
  );
});

InputSelectField.displayName = "InputSelectField";
export default InputSelectField;
